// Landing on the page a Hamyan notification pointed at.
//
// The backend's messaging core messages a member inside their messenger
// instead of texting them, and the message has to open the right page INSIDE
// the mini app. Two shapes arrive, one per messenger:
//
//   Eitaa   a text link  eitaa.com/hamyan_minibot_app/start?startapp=n_<token>
//           Its bot messages take no buttons, and a start param is
//           [A-Za-z0-9_-] only — it cannot hold `/fund/12?x=1` — so it carries
//           a short token that the backend trades for the path.
//   Bale    an inline button whose URL already IS the page, plus `?n=<token>`.
//           Nothing to resolve; the token only lets us report the tap, so both
//           messengers give the fund's history the same «دیده شد».
//
// Both go to `POST /messaging/open/`, which is what stamps the delivery opened.

import { miniappOpenNotification } from 'services/miniapp'
import { queueEvent } from 'utilities/analytics'
import { getRememberedPlatform, getStartParam } from './detect'

/**
 * What marks a start param as a notification's.
 *
 * `startapp` is one shared slot — the قرعه‌کشی landing owns `q_` — so every use
 * of it claims a prefix. Must match `messaging.links.START_PARAM_PREFIX`.
 */
export const NOTIFICATION_START_PARAM_PREFIX = 'n_'
/** Must match `messaging.links.OPEN_QUERY_PARAM`. */
export const NOTIFICATION_QUERY_PARAM = 'n'

/** How long first paint may wait on the backend for the page. */
export const NOTIFICATION_OPEN_TIMEOUT_MS = 2500

/** Tokens are ours, but they arrive in a URL: anything else is discarded. */
const TOKEN_PATTERN = /^[A-Za-z0-9]{6,16}$/

const asToken = (raw?: string | null): string | null =>
    raw && TOKEN_PATTERN.test(raw) ? raw : null

/** The token of an Eitaa-style launch (`?startapp=n_<token>`), if this is one. */
export const notificationTokenFromStartParam = (): string | null => {
    const param = getStartParam()
    if (!param || !param.startsWith(NOTIFICATION_START_PARAM_PREFIX)) return null
    return asToken(param.slice(NOTIFICATION_START_PARAM_PREFIX.length))
}

/** The token riding on a Bale-style launch that is already on the page. */
export const notificationTokenFromQuery = (): string | null =>
    asToken(
        new URLSearchParams(window.location.search).get(
            NOTIFICATION_QUERY_PARAM
        )
    )

/**
 * Only a same-origin absolute path is ever navigated to.
 *
 * It comes from our own backend, but it is still input: `//host/…` is a
 * protocol-relative URL and a full URL is somebody else's site.
 */
const isAppPath = (path: unknown): path is string =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')

/**
 * The page this launch should open, when it came from a notification link.
 *
 * Needs a session — the backend only answers for the member the message was
 * sent to — so call it after sign-in. Bounded, because it sits in front of
 * first paint, and silent: no answer means the app opens on its front door,
 * which is what that link did before any of this existed.
 */
export const notificationPath = async (): Promise<string | null> => {
    const token = notificationTokenFromStartParam()
    if (!token) return null
    try {
        const answer = await Promise.race([
            miniappOpenNotification(token),
            new Promise<null>((resolve) =>
                window.setTimeout(
                    () => resolve(null),
                    NOTIFICATION_OPEN_TIMEOUT_MS
                )
            ),
        ])
        if (!answer || !isAppPath(answer.path)) {
            queueEvent('miniapp_notification_open_failed', {
                platform: getRememberedPlatform(),
                reason: answer ? 'no_path' : 'timeout',
            })
            return null
        }
        queueEvent('miniapp_notification_opened', {
            platform: getRememberedPlatform(),
            notification_kind: answer.kind,
            via: 'start_param',
        })
        return answer.path
    } catch {
        queueEvent('miniapp_notification_open_failed', {
            platform: getRememberedPlatform(),
            reason: 'error',
        })
        return null
    }
}

/**
 * Tell the backend a Bale-style notification button was tapped.
 *
 * Fire-and-forget: the page is already open, so nothing waits on this and a
 * failure costs one «دیده شد» in the history, nothing the user can see.
 */
export const reportNotificationOpen = (): void => {
    const token = notificationTokenFromQuery()
    if (!token) return
    miniappOpenNotification(token)
        .then((answer) => {
            queueEvent('miniapp_notification_opened', {
                platform: getRememberedPlatform(),
                notification_kind: answer?.kind,
                via: 'button',
            })
        })
        .catch(() => undefined)
}
